/**
 * Utility functions for XML document parsing
 */

/**
 * Extracts XML content from a potentially markdown-wrapped response
 */
function extractXmlContent(input: string): string {
  const markdownCodeBlockRegex = /```(?:xml|markup|html)?\s*([\s\S]*?)```/;
  const markdownMatches = input.match(markdownCodeBlockRegex);
  if (markdownMatches && markdownMatches[1]) {
    return markdownMatches[1].trim();
  }

  const singleTickRegex = /`(?:xml|markup|html)?\s*([\s\S]*?)`/;
  const singleTickMatches = input.match(singleTickRegex);
  if (singleTickMatches && singleTickMatches[1]) {
    return singleTickMatches[1].trim();
  }

  if (input.trim().startsWith('<')) {
    return input.trim();
  }

  const xmlContentRegex = /(<[\s\S]*?>[\s\S]*?<\/[\s\S]*?>)/;
  const xmlContentMatches = input.match(xmlContentRegex);
  if (xmlContentMatches && xmlContentMatches[1]) {
    return xmlContentMatches[1].trim();
  }

  return input;
}

/**
 * Escapes XML special characters to ensure valid parsing
 * TODO feels like there should be a better way to do this
 */
function escapeXmlSpecialChars(str: string): string {
  return str.replace(/&(?!(?:amp);)/g, '&amp;');
}

/**
 * Extracts and parses an XML document from a string.
 * 1. Extracts XML content from the input string
 * 2. Escapes XML special characters
 * 3. Parses the sanitized string into an XML document
 * 4. Throws an error if parsing fails
 */
export function extractXmlDocFromString(xmlString: string) {
  const sanitizedString = escapeXmlSpecialChars(extractXmlContent(xmlString));

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(sanitizedString, 'text/xml');

  const parserErrors = xmlDoc.getElementsByTagName('parsererror');
  if (parserErrors.length > 0) {
    throw new Error(`XML parsing error: ${parserErrors[0].textContent}`);
  }

  return xmlDoc;
}
